import React from 'react';
import { MenuItem, Select, FormControl, SelectChangeEvent } from '@mui/material';
import { useTranslation } from './useTranslation';
import { SupportedLanguage, SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGE_CODES } from './translations';

interface LanguageOption {
  code: SupportedLanguage;
  label: string;
  flag: string;
}

// Derive the language options from the centralized config
const languages: LanguageOption[] = SUPPORTED_LANGUAGE_CODES.map(code => ({
  code,
  label: SUPPORTED_LANGUAGES[code].nativeName,
  flag: SUPPORTED_LANGUAGES[code].flag,
}));

interface LanguageSelectorProps {
  /** Show flag emoji alongside language name */
  showFlag?: boolean;
  /** Show only flag (compact mode) */
  flagOnly?: boolean;
  /** Size of the select */
  size?: 'small' | 'medium';
  /** Custom className */
  className?: string;
  /** Update user preferences */
  updateUser?: boolean;
}

/**
 * Language selector dropdown component.
 *
 * Usage:
 * ```tsx
 * <LanguageSelector />
 * <LanguageSelector showFlag />
 * <LanguageSelector flagOnly size="small" />
 * ```
 */
export function LanguageSelector ({
  showFlag = true,
  flagOnly = false,
  updateUser = false,
  size = 'small',
  className
}: LanguageSelectorProps) {
  const { language, setLanguage } = useTranslation();

  const handleChange = (event: SelectChangeEvent) => {
    setLanguage(event.target.value as SupportedLanguage, updateUser);
  };

  const renderValue = (selected: string) => {
    const lang = languages.find(l => l.code === selected);
    if (!lang) return selected;

    if (flagOnly) {
      return lang.flag;
    }

    return showFlag ? `${lang.flag} ${lang.label}` : lang.label;
  };

  return (
    <FormControl size={size} className={className}>
      <Select
        value={language}
        onChange={handleChange}
        renderValue={renderValue}
        sx={{ minWidth: flagOnly ? 60 : 120 }}
      >
        {languages.map((lang) => (
          <MenuItem key={lang.code} value={lang.code}>
            {showFlag && <span style={{ marginRight: 8 }}>{lang.flag}</span>}
            {lang.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
